import {
  prisma,
  type Prisma,
  DocumentEntityType,
  DocumentType,
  ParticipantGroupType,
} from "client";
import { getParticipantEntityIdForPortal } from "../product";

// CREATE

export const createDocument = async (data: Prisma.DocumentCreateInput) => {
  try {
    return await prisma.document.create({ data });
  } catch (error) {
    throw error;
  }
};

// GET

export const getDocument = async (id: string) => {
  return await prisma.document.findUnique({ where: { id } });
};

export const getDocumentsByEntityId = async (
  entityId: string,
  lang: string,
  search?: string,
  entityType?: DocumentEntityType,
  type?: DocumentType,
  getAllTranslations?: boolean,
  userId?: string,
  eventId?: string
) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const searchParam = search ? escapeRegex(search) : "";
  const filter = searchParam
    ? {
        OR: [
          {
            translations: {
              some: {
                OR: [
                  {
                    name: {
                      contains: searchParam,
                      mode: "insensitive" as const,
                    },
                  }, // Add 'as const'
                ],
              },
            },
          },
        ],
      }
    : {};

  const documents = await prisma.document.findMany({
    where: {
      entityId,
      ...filter,
      ...(entityType ? { entityType } : {}),
      ...(type ? { type } : {}),
    },
    include: {
      // Without userId, Prisma drops the where clause and returns every user's bookmarks.
      // Keep an empty array shape so callers can still read `.bookmarks.length`.
      bookmarks: userId
        ? {
            where: {
              userId,
              ...(eventId ? { eventId } : {}),
            },
          }
        : false,
      translations: getAllTranslations
        ? true
        : {
            where: { language: { code: lang } },
          },
    },
  });
  return documents.map((document) => ({
    ...document,
    bookmarks: userId ? (document.bookmarks ?? []) : [],
    name: document.translations?.[0]?.name,
    url: document.translations?.[0]?.url,
  }));
};

// UPDATE

export const updateDocument = async (
  id: string,
  data: Prisma.DocumentUpdateInput
) => {
  try {
    return await prisma.document.update({ where: { id }, data });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// DELETE

export const deleteDocument = async (id: string) => {
  return await prisma.document.delete({ where: { id } });
};

export type PortalDocumentRow = {
  id: string;
  name: string;
  url: string;
  type: DocumentType | null;
  translationId?: string;
  languageId?: string;
  translations: {
    id: string;
    name: string;
    url: string;
    languageId: string;
  }[];
};

export type DocumentSearchPage = {
  items: PortalDocumentRow[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export const DOCUMENT_PAGE_SIZE = 12;
/** Single-request cap for legacy admin lists (DocHeader / documentStore). */
export const LEGACY_MEDIA_LIST_CAP = 500;

const portalDocumentSelect = {
  id: true,
  type: true,
  translations: {
    select: {
      id: true,
      name: true,
      url: true,
      languageId: true,
      language: { select: { code: true } },
    },
  },
} as const;

const mapPortalDocuments = (
  documents: {
    id: string;
    type: DocumentType | null;
    translations: {
      id: string;
      name: string;
      url: string;
      languageId: string;
      language: { code: string };
    }[];
  }[],
  locale: string
): PortalDocumentRow[] =>
  documents.flatMap((document) => {
    if (!document.translations.length) return [];
    const display =
      document.translations.find((t) => t.language.code === locale) ??
      document.translations[0];
    return [
      {
        id: document.id,
        type: document.type,
        name: display.name,
        url: display.url,
        translationId: display.id,
        languageId: display.languageId,
        translations: document.translations.map(
          ({ id, name, url, languageId }) => ({
            id,
            name,
            url,
            languageId,
          })
        ),
      },
    ];
  });

const buildDocumentSearchWhere = (
  entityId: string,
  search?: string,
  entityType?: DocumentEntityType,
  documentType?: DocumentType
) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const searchParam = search?.trim() ? escapeRegex(search.trim()) : "";
  return {
    entityId,
    ...(entityType ? { entityType } : {}),
    ...(documentType ? { type: documentType } : {}),
    translations: searchParam
      ? {
          some: {
            name: {
              contains: searchParam,
              mode: "insensitive" as const,
            },
          },
        }
      : { some: {} },
  };
};

export const searchDocumentsByEntityId = async ({
  entityId,
  locale,
  search = "",
  page = 1,
  pageSize = DOCUMENT_PAGE_SIZE,
  entityType,
  documentType,
  all = false,
}: {
  entityId: string;
  locale: string;
  search?: string;
  page?: number;
  pageSize?: number;
  entityType?: DocumentEntityType;
  documentType?: DocumentType;
  all?: boolean;
}): Promise<DocumentSearchPage> => {
  const where = buildDocumentSearchWhere(
    entityId,
    search,
    entityType,
    documentType
  );
  const safePage = all ? 1 : Math.max(1, page);
  const safePageSize = all
    ? LEGACY_MEDIA_LIST_CAP
    : Math.min(Math.max(1, pageSize), 50);

  const [total, documents] = await Promise.all([
    prisma.document.count({ where }),
    prisma.document.findMany({
      where,
      select: portalDocumentSelect,
      orderBy: { createdAt: "desc" },
      skip: all ? 0 : (safePage - 1) * safePageSize,
      take: safePageSize,
    }),
  ]);

  return {
    items: mapPortalDocuments(documents, locale),
    total,
    page: safePage,
    pageSize: safePageSize,
    hasMore: safePage * safePageSize < total,
  };
};

export const getParticipantDocumentsForPortal = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType,
  locale: string,
  entityType: DocumentEntityType,
  search = "",
  page = 1,
  pageSize = DOCUMENT_PAGE_SIZE,
  documentType?: DocumentType
) => {
  const participant = await getParticipantEntityIdForPortal(
    slug,
    userId,
    eventId,
    type
  );
  if (!participant) return null;

  const documentsPage = await searchDocumentsByEntityId({
    entityId: participant.id,
    locale,
    search,
    page,
    pageSize,
    entityType,
    documentType,
  });

  return { id: participant.id, ...documentsPage };
};

export const assertDocumentBelongsToEntity = async (
  documentId: string,
  entityId: string
) => {
  return prisma.document.findFirst({
    where: { id: documentId, entityId },
    select: { id: true, entityType: true },
  });
};
